import {
  showPreClearance,
  editPreClearance,
  createPreClearance,
  storePreClearance,
  updatePreClearance,
} from '@app/api/pre-clearance.api';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { notificationController } from '@app/controllers/notificationController';
import { PreClearanceRequest } from '@app/models/pre_clearance/PreClearanceRequest';
import { Form, notification, Tabs } from 'antd';
import { Tab } from 'rc-tabs/lib/interface';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PreClearanceInfo from './PreClearanceInfo';
import AdditionalInfo, { PreClearanceField, ADDITIONAL_INFO_KEY } from './AdditionalInfo';
import CommentsTab from '../common/preClearance/Comments';
import DocumentChecklist from '../shared/DocumentChecklist';
import { DropdownOption } from '@app/models/DropdownOption';
import { Customer } from '@app/models/customer/Customer';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useTranslation } from 'react-i18next';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { CustomerInfoForm } from '@app/components/forms/CustomerInfoForm/CustomerInfoForm';
import GenerateDocumentsButton from './GenerateDocuments';
import CertificateList from '@app/components/apps/PreClearance/AllPRCertificates';
import Configuration from './Configuration';
import Extension from './Extention';
import WorkflowButtons from './WorkFlowButtons';
import { CustomerDTO } from '@app/models/customer/CustomerDTO';
import { PreClearanceFile } from '@app/models/pre_clearance/PreClearanceResponse';
import ExtensionInfo from './ExtensionInfo';

interface TypeOfForm {
  type: 'edit' | 'create' | 'view';
}

interface PreClearanceFormProps extends TypeOfForm {
  editable: boolean;
  id?: string;
  showEditIcon?: boolean;
  pageTitle: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setCurrentBreadcrumName: Function;
}

const parseUploadableTypes = (raw: unknown): string[] => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== 'string') return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Category + product document collections, filtered by pre-clearance type, de-duplicated by document_id
const getUploadableDocuments = (preClearance: any) => {
  const pcType = preClearance?.type;
  const all = [
    ...(preClearance?.product_category?.pre_clearance_document_collections ?? []),
    ...(preClearance?.product?.pre_clearance_document_collections ?? []),
  ];

  return all
    .filter((doc: any) => {
      const types = parseUploadableTypes(doc.uploadable_types);
      return !types.length || !pcType || types.includes(pcType);
    })
    .filter((doc: any, i: number, arr: any[]) => arr.findIndex((d) => d.document_id === doc.document_id) === i);
};

const PreClearanceForm: React.FC<PreClearanceFormProps> = ({
  editable = false,
  id,
  pageTitle,
  type,
  setCurrentBreadcrumName,
}) => {
  const [form] = Form.useForm();
  const [isLoading, setLoading] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const navigate = useNavigate();
  const preClearanceId = parseInt(id ?? '');
  const [referenceCode, setReferenceCode] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [comments, setComments] = useState([]);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [location_options, setLocationOptions] = useState<DropdownOption[]>([]);
  const [countries, setCountries] = useState<DropdownOption[]>([]);
  const [department_options, setDepartmentOptions] = useState<DropdownOption[]>([]);
  const [productCategories, setProductCategories] = useState<DropdownOption[]>([]);
  const [specialConditions, setSpecialConditions] = useState('');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userType = user?.usertype;
  const { t } = useTranslation();
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const [wfActions, setWfActions] = useState<string[]>([]);
  const [ExtensionwfActions, setExtensionWfActions] = useState<string[]>([]);
  const [DocFormFields, setDocFormFields] = useState<any[]>([]);
  const [IniDocFormFields, setIniDocFormFields] = useState<any[]>([]);
  const [preClearanceDetails, setPreClearanceDetails] = useState({
    job_id: '',
    job_type: '',
    status: '',
  });
  const [hasLetter, setHasLetter] = useState(false);
  const [hasCertificate, setHasCertificate] = useState(false);
  const [hasAnnex, setHasAnnex] = useState(false);
  const [updateConfigInfo, setUpdateConfigInfo] = useState(false);
  const [customerData, setCustomerInfo] = useState<CustomerDTO | null>(null);
  const [extensionExpiryDate] = useState<string | null>(null);
  const [statusRefreshKey] = useState(0);
  const [categoryFields, setCategoryFields] = useState<PreClearanceField[]>([]);
  const [productFields, setProductFields] = useState<PreClearanceField[]>([]);
  const [preClearanceFile, setPreClearanceFile] = useState<PreClearanceFile | PreClearanceFile[] | null>(null);
  const [preClearanceExtension, setPreClearanceExtension] = useState<any>(null);
  const [canExtend, setCanExtend] = useState(false);
  const [activeTab, setActiveTab] = useState('1');

  const shouldShowEditButton = () => {
    if (userType === 'ADMIN') {
      return;
    } else if (userType === 'CUSTOMER') {
      return status === 'DRAFT' || status === 'PENDING_DOCUMENT';
    }
    return false;
  };

  const items: Tab[] | undefined = [
    {
      key: '1',
      label: 'Customer Info',
      children: <CustomerInfoForm form={form} type={type} customerInfo={customerData} createType="PC" />,
    },
    {
      key: '2',
      label: 'Pre-Clearance Info',
      forceRender: true,
      children: (
        <PreClearanceInfo
          editable={editable}
          status={status}
          productCategories={productCategories}
          countries={countries}
          locationOptions={location_options}
        />
      ),
    },
    ...(status !== null && status !== 'DRAFT'
      ? [
          {
            key: '3',
            label: 'Additional Info',
            forceRender: true,
            children: (
              <AdditionalInfo editable={editable} categoryFields={categoryFields} productFields={productFields} />
            ),
          },
        ]
      : []),
    ...(status !== null && status !== 'DRAFT'
      ? [
          {
            key: '4',
            label: t('Document Checklist'),
            children: (
              <DocumentChecklist
                initialformFields={IniDocFormFields}
                formFields={DocFormFields}
                status={status}
                pre_clearance_id={id ?? null}
              />
            ),
          },
        ]
      : []),
    ...(user?.id == 2 && status !== 'DRAFT' && status !== 'PENDING_DOCUMENT' && status !== 'PENDING_REVIEW'
      ? [
          {
            key: '5',
            label: 'Configuration Info',
            children: (
              <Configuration
                editable={editable}
                updateConfigInfo={updateConfigInfo}
                isStatusDoReview={status == 'DOREVIEW'}
                form={form}
                preClearanceData={undefined}
                extensionExpire={extensionExpiryDate}
                pre_clearance_id={id ?? null}
                status={status}
              />
            ),
          },
        ]
      : []),
    ...(status == 'DONE' || status == 'PENDING_APPROVAL'
      ? [
          {
            key: '6',
            label: t('Documents'),
            children: <CertificateList id={id ?? null} files={preClearanceFile} />,
          },
        ]
      : []),
    ...(status == 'DONE' && preClearanceExtension
      ? [
          {
            key: '7',
            label: t('Extension'),
            children: <ExtensionInfo extension={preClearanceExtension} />,
          },
        ]
      : []),
    ...(type !== 'create' && status !== 'DRAFT'
      ? [
          {
            key: '8',
            label: t('Comments'),
            children: (
              <CommentsTab editable={editable} status={status} commentData={comments} pre_clearance_id={id ?? null} />
            ),
          },
        ]
      : []),
  ];

  function setCustomerData(customer: Customer) {
    setCustomerId(customer?.id);
    form.setFieldsValue({
      id: customer.id,
      email: customer.user?.email || '',
      name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim(),
      status: customer.user?.status || '',
      customer_type: customer.customer_type || '',
      contact_no: customer.contact_no || '',
      address: customer.address || '',
      nic: customer.nic || '',
      dob: customer.dob || '',
    });
  }

  const setApiData = (res: any) => {
    const preClearance = res?.pre_clearance;

    setCurrentBreadcrumName(`${preClearance?.code ?? 'New'}`);
    setCode(preClearance?.code ?? 'N/A');
    setReferenceCode(preClearance?.code ?? 'N/A');
    setStatus(preClearance?.status ?? 'New');
    setSpecialConditions(preClearance?.additionalInfo?.special_conditions ?? '');
    // Create/edit return the full list as `countries`; view returns `country_options`.
    // Saved countries are added so their names always resolve in view mode.
    const savedCountries: DropdownOption[] = (preClearance?.countries ?? []).map((c: any) => ({
      value: c.id,
      label: c.name,
    }));
    const allCountries: DropdownOption[] = [
      ...(res?.countries ?? []),
      ...(res?.country_options ?? []),
      ...savedCountries,
    ];
    setCountries(allCountries.filter((c, i, arr) => arr.findIndex((o) => o.value === c.value) === i));
    setComments(preClearance?.pre_clearance_comments ?? []);
    setDepartmentOptions(res?.department_options ?? []);
    setLocationOptions(res?.location_options ?? []);
    setProductCategories(res?.product_category_options ?? []);
    setCustomerId(preClearance?.customer?.id ?? user?.customer?.id);
    setIniDocFormFields(preClearance?.pre_clearance_documents || []);
    setDocFormFields(getUploadableDocuments(preClearance));
    setWfActions(res?.wf_actions ?? []);
    setPreClearanceDetails({
      job_id: preClearance?.id ?? 'N/A',
      job_type: res?.pre_clearance_category_options?.map((category: any) => category.label).join(', ') ?? '',
      status: preClearance?.status ?? '',
    });

    setHasLetter(res?.generates?.LETTER ?? false);
    setHasCertificate(res?.generates?.CERTIFICATE ?? false);
    setHasAnnex(res?.generates?.EXTENSION ?? false);
    setCustomerInfo(preClearance?.customer ?? null);
    setCanExtend(preClearance?.can_extend ?? false);
    setUpdateConfigInfo(preClearance?.can_edit_config_info ?? false);
    setCategoryFields(preClearance?.product_category?.pre_clearance_fields ?? []);
    setProductFields(preClearance?.product?.pre_clearance_fields ?? []);
    setPreClearanceExtension(preClearance?.pre_clearance_extension ?? null);
    setPreClearanceFile(preClearance?.pre_clearance_files ?? null);
    setExtensionWfActions(res?.extention_wf_actions ?? []);

    if (preClearance?.customer) {
      setCustomerData(preClearance.customer);
    } else {
      setCustomerData({
        id: user.customer.id,
        user: {
          email: user.email || '',
          status: user.status || '',
          id: 0,
          name: '',
          email_verified_at: undefined,
          created_at: '',
          updated_at: '',
          username: '',
          usertype: '',
          image_url: '',
          customer: {
            pr_options: [],
            pc_options: [],
            id: 0,
            user_id: 0,
            customer_type: '',
            first_name: '',
            last_name: '',
            contact_no: '',
            address: '',
            dob: '',
            nic: '',
            verified: false,
            created_at: '',
            updated_at: '',
            name: '',
            additional_info: undefined,
            user: undefined,
          },
        },
        customer_type: user.customer.customer_type || '',
        contact_no: user.customer.contact_no || '',
        address: user.customer.address || '',
        nic: user.customer.nic || '',
        dob: user.customer.dob || '',
        user_id: 0,
        first_name: user.customer.first_name || '',
        last_name: user.customer.last_name || '',
        verified: false,
        created_at: '',
        updated_at: '',
        name: `${user.customer.first_name || ''} ${user.customer.last_name || ''}`.trim(),
        pr_options: [],
        pc_options: [],
      });
    }

    form.setFieldsValue({
      reference_code: preClearance?.reference_code ?? '',
      status: preClearance?.status,
      customer_id: customerId,

      // Pre-Clearance Info
      product_category_id: preClearance?.product_category_id,
      product_id: preClearance?.product_id,
      country_ids: preClearance?.countries?.map((e: any) => e?.id) ?? [],
      location_id: preClearance?.location_id,
      department_id: preClearance?.department_id,
      invoice_no: preClearance?.invoice_no,
      invoice_date: preClearance?.invoice_date,
      quantity: preClearance?.quantity,
      hs_code: preClearance?.hs_code,
      value_usd: preClearance?.value_usd,
      supplier_name: preClearance?.supplier_name,
      supplier_reg: preClearance?.supplier_reg,
      supplier_address: preClearance?.supplier_address,
      importer_name: preClearance?.importer_name,
      importer_address: preClearance?.importer_address,
      pc_business_name: preClearance?.business_name,
      pc_business_address: preClearance?.business_address,

      [ADDITIONAL_INFO_KEY]: preClearance?.pre_clearance_info?.informations ?? {},

      // Configuration Info
      validity_period: preClearance?.validity_period,
      validity_exp: preClearance?.validity_exp,
      special_conditions: preClearance?.special_conditions,
      expires_at: preClearance?.expires_at,
      certificate_name: preClearance?.certificate_name,
      letter_name: preClearance?.letter_name,
      extension_name: preClearance?.extension_name,
    });
  };

  // useEffect(() => {
  //   setLoading(true);

  //   const fetchData = async () => {
  //     try {
  //       const res =
  //         preClearanceId && type === 'view'
  //           ? await showPreClearance(preClearanceId)
  //           : preClearanceId && type === 'edit'
  //           ? await editPreClearance(preClearanceId)
  //           : type === 'create'
  //           ? await createPreClearance()
  //           : null;

  //       if (res) setApiData(res);
  //     } catch (err: any) {
  //       notification.error({ message: 'Failed to fetch product data', description: err.message });
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchData();
  // }, [preClearanceId, type, status, id]);

  const handleSubmit = (values: PreClearanceRequest) => {
    if (!editable) return;

    const get = (name: string) => form.getFieldValue(name);

    const submissionData = {
      ...form.getFieldsValue(),
      ...values,
      reference_code: String(referenceCode),
      customer_id: customerId,
      type: localStorage.getItem('selected_pc_type') ?? '',

      // Pre-Clearance Info
      product_category_id: get('product_category_id') || null,
      product_id: get('product_id') || null,
      country_ids: get('country_ids') ?? [],
      location_id: get('location_id') || null,
      department_id: get('department_id') || null,
      invoice_no: get('invoice_no') || '',
      invoice_date: get('invoice_date') || '',
      quantity: get('quantity') ?? null,
      hs_code: get('hs_code') || '',
      value_usd: get('value_usd') ?? null,
      supplier_name: get('supplier_name') || '',
      supplier_reg: get('supplier_reg') || '',
      supplier_address: get('supplier_address') || '',
      importer_name: get('importer_name') || '',
      importer_address: get('importer_address') || '',
      business_name: get('pc_business_name') || '',
      business_address: get('pc_business_address') || '',

      [ADDITIONAL_INFO_KEY]: get(ADDITIONAL_INFO_KEY) ?? {},

      // Configuration Info
      validity_period: get('validity_period') || '',
      validity_exp: get('validity_exp') || '',
      special_conditions: get('special_conditions') || '',
      expires_at: get('expires_at') || '',
    };

    if (type === 'create') {
      storePreClearance(submissionData)
        .then((res: any) => {
          setLoading(false);
          navigate(`/view-pre-clearance`, { state: { id: res?.data?.id } });
        })
        .catch((err: any) => {
          notificationController.error({ message: err.message });
          setLoading(false);
        });
    } else if (type === 'edit' && preClearanceId) {
      updatePreClearance(preClearanceId, submissionData)
        .then(() => {
          setLoading(false);
          navigate(`/view-pre-clearance`, { state: { id: id } });
        })
        .catch((err: any) => {
          notificationController.error({ message: `${err.message}` });
          setLoading(false);
        });
    }
  };

  const hasAnyDocument = hasLetter || hasCertificate || hasAnnex;

  const MAX_LISTED_ERRORS = 8;

  const handleValidationError = (errorInfo: any) => {
    const errorFields: { name: (string | number)[]; errors: string[] }[] = errorInfo?.errorFields ?? [];
    if (!errorFields.length) return;

    const messages = errorFields.map((f) => f.errors?.[0]).filter(Boolean);
    const extra = messages.length - MAX_LISTED_ERRORS;

    notificationController.error({
      message: 'Please complete the required fields',
      description: (
        <ul style={{ margin: 0, paddingLeft: 16 }}>
          {messages.slice(0, MAX_LISTED_ERRORS).map((msg) => (
            <li key={msg}>{msg}</li>
          ))}
          {extra > 0 && <li>…and {extra} more</li>}
        </ul>
      ),
    });

    // Jump to the tab holding the first invalid field, then scroll to it
    const firstName = errorFields[0].name;
    setActiveTab(firstName[0] === ADDITIONAL_INFO_KEY ? '3' : '2');
    setTimeout(() => form.scrollToField(firstName, { behavior: 'smooth', block: 'center' }), 100);
  };

  useEffect(() => {
    const buttons = {
      'save-button': editable && (
        <BaseButton
          type="primary"
          loading={isLoading}
          size="small"
          onClick={() => {
            form
              .validateFields()
              .then((values) => {
                handleSubmit(values);
              })
              .catch(handleValidationError);
          }}
        >
          {t('Save')}
        </BaseButton>
      ),
      'cancel-button': editable && (
        <BaseButton onClick={() => navigate('/pre-clearance')} size="small" style={{ marginRight: '8px' }}>
          {t('Cancel')}
        </BaseButton>
      ),
      'edit-button': type === 'view' && preClearanceId && shouldShowEditButton() && (
        <BaseButton
          type="primary"
          onClick={() => navigate('/edit-pre-clearance', { state: { id: id } })}
          size="small"
          style={{ marginRight: '8px' }}
        >
          {t('Edit')}
        </BaseButton>
      ),
      'generate-documents': type === 'view' && preClearanceId && hasAnyDocument && (
        <GenerateDocumentsButton
          hasLetter={hasLetter}
          hasCertificate={hasCertificate}
          hasAnnex={hasAnnex}
          onRefresh={refreshPreClearance}
          id={preClearanceId}
        />
      ),
      'request-extension': type === 'view' && preClearanceId && (
        <Extension
          preClearanceId={preClearanceId}
          userType={user?.usertype}
          preClearanceExtension={preClearanceExtension}
          canExtend={canExtend}
          ExtensionwfActions={ExtensionwfActions}
        />
      ),
    };

    Object.entries(buttons).forEach(([key, button]) => {
      if (button) addHeaderButton(key, button);
    });

    return () => {
      Object.keys(buttons).forEach(removeHeaderButton);
    };
  }, [
    type,
    status,
    preClearanceId,
    editable,
    addHeaderButton,
    removeHeaderButton,
    t,
    navigate,
    isLoading,
    form,
    handleSubmit,
    id,
    preClearanceDetails,
    hasLetter,
    hasCertificate,
    hasAnnex,
    hasAnyDocument,
    ExtensionwfActions,
  ]);

  const refreshPreClearance = async () => {
    setLoading(true);
    try {
      const res =
        preClearanceId && type === 'view'
          ? await showPreClearance(preClearanceId)
          : preClearanceId && type === 'edit'
          ? await editPreClearance(preClearanceId)
          : type === 'create'
          ? await createPreClearance()
          : null;

      if (res) setApiData(res);
    } catch (err: any) {
      notification.error({ message: 'Failed to fetch pre-clearance data', description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshPreClearance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preClearanceId, type]);

  const handleStatusChange = (newStatus: string | null) => {
    setStatus(newStatus);
    refreshPreClearance();
  };

  return (
    <BaseRow gutter={[16, 16]}>
      <BaseCol span={24}>
        <WorkflowButtons
          id={id ?? null}
          key={statusRefreshKey}
          status={status}
          setStatus={handleStatusChange}
          wfActions={wfActions || []}
        />
      </BaseCol>

      <BaseCol span={24}>
        <BaseCard id="PreClearanceForm" title={pageTitle} padding="1.25rem">
          <BaseRow>
            <BaseCol span={24}>
              <Form
                form={form}
                name="PreClearanceForm"
                onFinish={handleSubmit}
                labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
                labelAlign="left"
                labelWrap
                wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
                size="small"
              >
                <Tabs
                  activeKey={activeTab}
                  onChange={setActiveTab}
                  items={type == 'create' ? items?.slice(0, 2) : items}
                />
              </Form>
            </BaseCol>
          </BaseRow>
        </BaseCard>
      </BaseCol>
    </BaseRow>
  );
};

export default PreClearanceForm;
